import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import './styles/adminTilmeldinger.css';

function AdminTilmeldinger() {
  const { kursusId, eventId } = useParams();
  const navigate = useNavigate();
  const [tilmeldinger, setTilmeldinger] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [redigerTilmelding, setRedigerTilmelding] = useState(null);
  const [visRedigerModal, setVisRedigerModal] = useState(false);
  const [kursusNavn, setKursusNavn] = useState("");
  const [maxDeltagere, setMaxDeltagere] = useState(0);
  const [antalTilmeldte, setAntalTilmeldte] = useState(0);
  const [visAlleEvents, setVisAlleEvents] = useState(false);

  useEffect(() => {
    const hentEvents = async () => {
      try {
        // Hent kursusnavn
        const kursusRef = doc(db, 'kurser', kursusId);
        const kursusDoc = await getDoc(kursusRef);
        if (kursusDoc.exists()) {
          setKursusNavn(kursusDoc.data().navn);
        }

        // Først henter vi alle events for dette kursus
        const eventsQuery = query(
          collection(db, 'events'), 
          where('kursusId', '==', kursusId)
        );
        const eventsSnapshot = await getDocs(eventsQuery);
        const eventsData = eventsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setEvents(eventsData);
        console.log('Events hentet:', eventsData);

        // Hvis der er et specifikt eventId i URL'en, vælg det
        if (eventId) {
          const selectedEventData = eventsData.find(e => e.id === eventId);
          if (selectedEventData) {
            setSelectedEvent(selectedEventData);
            setMaxDeltagere(selectedEventData.maxDeltagere);
            hentTilmeldinger(selectedEventData.id);
            return;
          }
        }

        // Ellers, hvis der er events, vælg det første som standard
        if (eventsData.length > 0) {
          setSelectedEvent(eventsData[0]);
          setMaxDeltagere(eventsData[0].maxDeltagere);
          hentTilmeldinger(eventsData[0].id);
        }
      } catch (error) {
        console.error('Fejl ved hentning af data:', error);
      }
    };

    hentEvents();
  }, [kursusId, eventId]);

  const hentTilmeldinger = async (eventId) => {
    try {
      console.log('Henter tilmeldinger for event:', eventId);
      const tilmeldingerQuery = query(
        collection(db, 'tilmeldinger'), 
        where('eventId', '==', eventId)
      );
      const tilmeldingerSnapshot = await getDocs(tilmeldingerQuery);
      const tilmeldingerData = tilmeldingerSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Beregn det totale antal tilmeldte
      const totalTilmeldte = tilmeldingerData.reduce((sum, tilmelding) => 
        sum + (tilmelding.antalDeltagere || 0), 0);
      setAntalTilmeldte(totalTilmeldte);
      
      setTilmeldinger(tilmeldingerData);
      console.log('Tilmeldinger hentet:', tilmeldingerData);
    } catch (error) {
      console.error('Fejl ved hentning af tilmeldinger:', error);
    }
  };

  const handleEventChange = (event) => {
    const selectedEventData = events.find(e => e.id === event.target.value);
    setSelectedEvent(selectedEventData);
    setMaxDeltagere(selectedEventData.maxDeltagere);
    hentTilmeldinger(event.target.value);
  };

  const handleSlet = async (tilmeldingId) => {
    if (window.confirm('Er du sikker på, at du vil slette denne tilmelding?')) {
      try {
        await deleteDoc(doc(db, 'tilmeldinger', tilmeldingId));
        await hentTilmeldinger(selectedEvent.id);
      } catch (error) {
        console.error('Fejl ved sletning:', error);
        alert('Der skete en fejl ved sletning af tilmeldingen');
      }
    }
  };

  const handleRedigerGem = async () => {
    try {
      await updateDoc(doc(db, 'tilmeldinger', redigerTilmelding.id), {
        virksomhedsnavn: redigerTilmelding.virksomhedsnavn,
        cvr: redigerTilmelding.cvr,
        ean: redigerTilmelding.ean,
        faktureringsemail: redigerTilmelding.faktureringsemail,
        deltagere: redigerTilmelding.deltagere,
        antalDeltagere: redigerTilmelding.deltagere.length
      });
      await hentTilmeldinger(selectedEvent.id);
      setVisRedigerModal(false);
    } catch (error) {
      console.error('Fejl ved opdatering:', error);
      alert('Der skete en fejl ved opdatering af tilmeldingen');
    }
  };

  const kanTilføjeDeltager = (currentTilmelding) => {
    // Beregn antal tilmeldte fra alle andre tilmeldinger (ekskl. den nuværende)
    const andreTilmeldte = tilmeldinger.reduce((sum, tilmelding) => {
      if (tilmelding.id === currentTilmelding.id) {
        return sum; // Spring over den nuværende tilmelding
      }
      return sum + (tilmelding.antalDeltagere || 0);
    }, 0);

    // Beregn om der er plads til flere, baseret på:
    // - Det totale antal pladser (maxDeltagere)
    // - Antal tilmeldte fra andre tilmeldinger (andreTilmeldte)
    // - Antal deltagere i den nuværende redigering (redigerTilmelding.deltagere.length)
    return (andreTilmeldte + redigerTilmelding.deltagere.length) < maxDeltagere;
  };

  const filtrerEvents = (events) => {
    if (!visAlleEvents) {
      const idag = new Date();
      idag.setHours(0, 0, 0, 0);
      
      return events.filter(event => {
        const eventDato = new Date(event.dato);
        return eventDato >= idag;
      });
    }
    return events;
  };

  return (
    <div className="admin-tilmeldinger">
      <button 
        onClick={() => navigate(-1)}
        className="tilbage-knap"
      >
        ← Tilbage
      </button>

      <div className="tilmeldinger-header">
        <h1>{kursusNavn} tilmeldinger</h1>
        
        {events.length > 0 && (
          <>
            <div className="event-filter">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={visAlleEvents}
                  onChange={(e) => setVisAlleEvents(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
              <span className="filter-label">
                {visAlleEvents ? 'Viser alle events' : 'Viser kun kommende events'}
              </span>
            </div>
            
            <div className="event-selector">
              <label>Vælg event:</label>
              <select 
                value={selectedEvent?.id || ''} 
                onChange={handleEventChange}
              >
                {filtrerEvents(events).map(event => (
                  <option key={event.id} value={event.id}>
                    {event.dato} - {event.tid}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {selectedEvent && (
          <div className="event-info">
            <p><strong>Dato:</strong> {selectedEvent.dato}</p>
            <p><strong>Tid:</strong> {selectedEvent.tid}</p>
            <p><strong>Lokation:</strong> {selectedEvent.lokation}</p>
            <p><strong>Tilmeldte:</strong> {antalTilmeldte}/{maxDeltagere}</p>
          </div>
        )}
      </div>

      <div className="tilmeldinger-liste">
        {tilmeldinger.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Virksomhed</th>
                <th>CVR</th>
                <th>Fakturering</th>
                <th>Antal deltagere</th>
                <th>Deltagere</th>
                <th>Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {tilmeldinger.map(tilmelding => (
                <tr key={tilmelding.id}>
                  <td>{tilmelding.virksomhedsnavn}</td>
                  <td>{tilmelding.cvr}</td>
                  <td>
                    {tilmelding.ean ? (
                      <>EAN: {tilmelding.ean}</>
                    ) : (
                      <>Email: {tilmelding.faktureringsemail}</>
                    )}
                  </td>
                  <td>{tilmelding.antalDeltagere}</td>
                  <td>
                    <ul className="deltager-liste">
                      {tilmelding.deltagere.map((deltager, index) => (
                        <li key={index}>
                          {deltager.navn} ({deltager.email})
                          {deltager.sagsnr && <span> - Sagsnr: {deltager.sagsnr}</span>}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td>
                    <div className="tilmelding-actions">
                      <button 
                        onClick={() => {
                          setRedigerTilmelding(tilmelding);
                          setVisRedigerModal(true);
                        }}
                        className="rediger-btn"
                      >
                        Rediger
                      </button>
                      <button 
                        onClick={() => handleSlet(tilmelding.id)}
                        className="slet-btn"
                      >
                        Slet
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="ingen-tilmeldinger">Der er endnu ingen tilmeldinger til dette event.</p>
        )}
      </div>

      {visRedigerModal && redigerTilmelding && (
        <div className="rediger-modal">
          <div className="rediger-modal-content">
            <h3>Rediger tilmelding</h3>
            <div className="rediger-form">
              <div className="form-group">
                <label>Virksomhedsnavn:</label>
                <input
                  type="text"
                  value={redigerTilmelding.virksomhedsnavn}
                  onChange={(e) => setRedigerTilmelding({
                    ...redigerTilmelding,
                    virksomhedsnavn: e.target.value
                  })}
                />
              </div>
              <div className="form-group">
                <label>CVR:</label>
                <input
                  type="text"
                  value={redigerTilmelding.cvr}
                  onChange={(e) => setRedigerTilmelding({
                    ...redigerTilmelding,
                    cvr: e.target.value
                  })}
                />
              </div>
              <div className="form-group">
                <label>EAN:</label>
                <input
                  type="text"
                  value={redigerTilmelding.ean || ''}
                  onChange={(e) => setRedigerTilmelding({
                    ...redigerTilmelding,
                    ean: e.target.value
                  })}
                />
              </div>
              <div className="form-group">
                <label>Faktureringsemail:</label>
                <input
                  type="email"
                  value={redigerTilmelding.faktureringsemail || ''}
                  onChange={(e) => setRedigerTilmelding({
                    ...redigerTilmelding,
                    faktureringsemail: e.target.value
                  })}
                />
              </div>
              
              <h4>Deltagere</h4>
              {redigerTilmelding.deltagere.map((deltager, index) => (
                <div key={index} className="deltager-form">
                  <div className="deltager-header">
                    <h4>Deltager {index + 1}</h4>
                    <button 
                      onClick={() => {
                        if (redigerTilmelding.deltagere.length > 1) {
                          const nyeDeltagere = [...redigerTilmelding.deltagere];
                          nyeDeltagere.splice(index, 1);
                          setRedigerTilmelding({
                            ...redigerTilmelding,
                            deltagere: nyeDeltagere,
                            antalDeltagere: nyeDeltagere.length
                          });
                        } else {
                          alert('Der skal være mindst én deltager');
                        }
                      }}
                      className="slet-deltager-btn"
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                  <div className="form-group">
                    <label>Navn:</label>
                    <input
                      type="text"
                      value={deltager.navn}
                      onChange={(e) => {
                        const nyeDeltagere = [...redigerTilmelding.deltagere];
                        nyeDeltagere[index].navn = e.target.value;
                        setRedigerTilmelding({
                          ...redigerTilmelding,
                          deltagere: nyeDeltagere
                        });
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email:</label>
                    <input
                      type="email"
                      value={deltager.email}
                      onChange={(e) => {
                        const nyeDeltagere = [...redigerTilmelding.deltagere];
                        nyeDeltagere[index].email = e.target.value;
                        setRedigerTilmelding({
                          ...redigerTilmelding,
                          deltagere: nyeDeltagere
                        });
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Sagsnummer:</label>
                    <input
                      type="text"
                      value={deltager.sagsnr || ''}
                      onChange={(e) => {
                        const nyeDeltagere = [...redigerTilmelding.deltagere];
                        nyeDeltagere[index].sagsnr = e.target.value;
                        setRedigerTilmelding({
                          ...redigerTilmelding,
                          deltagere: nyeDeltagere
                        });
                      }}
                    />
                  </div>
                </div>
              ))}
              
              <button 
                onClick={() => {
                  if (kanTilføjeDeltager(redigerTilmelding)) {
                    const nyeDeltagere = [...redigerTilmelding.deltagere];
                    nyeDeltagere.push({
                      navn: '',
                      email: '',
                      sagsnr: ''
                    });
                    setRedigerTilmelding({
                      ...redigerTilmelding,
                      deltagere: nyeDeltagere,
                      antalDeltagere: nyeDeltagere.length
                    });
                  } else {
                    alert(`Der er ikke plads til flere deltagere. Maximum er ${maxDeltagere} deltagere.`);
                  }
                }}
                className="tilføj-deltager-btn"
                type="button"
                disabled={!kanTilføjeDeltager(redigerTilmelding)}
              >
                + Tilføj deltager {kanTilføjeDeltager(redigerTilmelding) ? 
                  '' : 
                  '(Max antal nået)'}
              </button>

              <div className="modal-buttons">
                <button onClick={handleRedigerGem} className="gem-btn">
                  Gem ændringer
                </button>
                <button onClick={() => setVisRedigerModal(false)} className="annuller-btn">
                  Annuller
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTilmeldinger;
